import { Edit3, RotateCcw, Scissors } from 'lucide-react'

import type { CreditNoteMode } from '@/types'
import { cn } from '@/lib/utils'

interface CreditNoteModeCardsProps {
  value: CreditNoteMode
  onChange: (value: CreditNoteMode) => void
}

interface ModeCardSpec {
  value: CreditNoteMode
  title: string
  subtitle: string
  Icon: typeof RotateCcw
}

const MODES: ModeCardSpec[] = [
  {
    value: 'full_cancellation',
    title: 'Annuler une facture',
    subtitle: "Totale d'une facture émise",
    Icon: RotateCcw,
  },
  {
    value: 'partial_proportional',
    title: 'Annuler partiellement',
    subtitle: 'Crédit proportionnel',
    Icon: Scissors,
  },
  {
    value: 'free_lines',
    title: 'Note de crédit libre',
    subtitle: 'Geste commercial sans facture',
    Icon: Edit3,
  },
]

/**
 * Mode picker for SOLY-CN-001 credit notes. Three compact cards in a grid;
 * each card opens a distinct workflow underneath. Acts as a radio group —
 * arrow-key navigation, single selection, value reported via onChange.
 */
export function CreditNoteModeCards({ value, onChange }: CreditNoteModeCardsProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Type de note de crédit"
      className="grid grid-cols-1 gap-3 sm:grid-cols-3"
    >
      {MODES.map(({ value: mode, title, subtitle, Icon }) => {
        const isSelected = mode === value
        return (
          <button
            key={mode}
            type="button"
            role="radio"
            aria-checked={isSelected}
            data-testid={`credit-note-mode-card-${mode.replace(/_/g, '-')}`}
            onClick={() => onChange(mode)}
            className={cn(
              'flex flex-col items-start gap-1 rounded-lg border p-4 text-left transition-all min-h-[88px]',
              'hover:border-primary/50 focus:outline-none focus:ring-2 focus:ring-primary',
              isSelected
                ? 'border-primary bg-primary/5 ring-1 ring-primary'
                : 'border-border bg-card',
            )}
          >
            <div className="flex items-center gap-2">
              <Icon className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
              <span className="font-medium">{title}</span>
            </div>
            <span className="text-xs text-muted-foreground">{subtitle}</span>
          </button>
        )
      })}
    </div>
  )
}
